import Cookie from "js-cookie";

import { BACKEND_URI } from "@/config";

import type { AnalyticsEvent } from "../types";
import type { Destination } from "./types";

/**
 * Our own ingest endpoint, `POST /api/analytics/events`.
 *
 * ## No buffering
 *
 * Every event goes out immediately as a one-element batch. A page view is one
 * event per navigation and navigations are human-paced, so there is no burst to
 * smooth — a "flush at 20 events or 10 seconds" policy would flush a batch of one
 * nearly every time, and we would have built a buffer, a timer, unload handlers
 * and a flush-in-flight guard to save requests nobody was making.
 *
 * Buffering would also make delivery *worse*. Events are best-effort by design:
 * one sitting in memory when the tab is killed is lost, while one already on the
 * wire is not.
 *
 * `keepalive` is what actually does the work a buffer plus an unload flush was
 * meant to do — it stops the browser cancelling the request when the user
 * navigates away or closes the tab.
 *
 * Not `navigator.sendBeacon`: it cannot set headers, so every event from a
 * logged-in user would arrive at the server as a guest.
 *
 * ## The wire format is still an array
 *
 * `{ events: [...] }` even at length one, and the server keeps its batch
 * endpoint. That costs nothing today and means adding buffering later is a change
 * confined to this file, with no call sites touched.
 *
 * The trigger to actually do it: the first event type that fires faster than a
 * human clicks — scroll depth, field-level interactions, hover. Page views are
 * not that.
 *
 * ## Tests
 *
 * `src/test/setup.ts` stubs `globalThis.fetch`, so route tests exercise this
 * destination against the stub rather than the network. Nothing to disable.
 */

const ENDPOINT = "/api/analytics/events";

export const rebelbaseDestination: Destination = {
  name: "rebelbase",

  // Always on. There is no kill switch by design: this is first-party ingest to
  // our own endpoint, not a third-party script, and a flag would be one more
  // thing to get wrong per environment. If it ever needs turning off, that is a
  // server-side decision — the endpoint can stop accepting.
  isEnabled() {
    return true;
  },

  // The spine takes every event type. Filtering happens server-side, against the
  // name allowlist in RebelBase.Analytics.AnalyticsEvent.
  accepts() {
    return true;
  },

  send(event: AnalyticsEvent) {
    const token = Cookie.get("token");

    const headers: Record<string, string> = { "Content-Type": "application/json" };
    // Same token source as the axios instance in utils/api-calls. Absent for
    // guests, which the endpoint expects — it pipes :auth but not :ensure_auth
    // precisely so guest page views land.
    if (token) headers.Authorization = `Bearer ${token}`;

    const request: RequestInit = {
      method: "POST",
      keepalive: true,
      headers,
      body: JSON.stringify({ events: [event] }),
    };

    // Never retried, never surfaced. The server answers 202 even for a partially
    // rejected batch, so there is nothing here to act on.
    fetch(`${BACKEND_URI}${ENDPOINT}`, request).catch(() => {});
  },
};
